import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle, CheckCircle2, Circle, Clock,
  CreditCard, ExternalLink, Flag, Loader2, MessageCircle, Pencil, Printer, Receipt, RotateCcw, User, Wallet, X, XCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useFarmOrder, useOrderContext, useOrderDetail } from '../hooks/useFarmOrder';
import { getOrCreateThread } from '../hooks/useChat';
import { MATERIALS, MODELING_TYPES, QUOTE_FILE_ACCEPT, QUOTE_FILE_RE, QUOTE_FILE_HINT } from '../lib/machinesCatalog';
import { calcFarmPricing } from '../lib/pricing';
import { PageShell } from '@/components/layout/PageShell';
import { formatBRL, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { OrderStatus, FarmCosts, FarmMachine } from '../types';

// ─── Labels ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<OrderStatus, string> = {
  quote:     'Aguardando orçamento',
  reserved:  'Reservado',
  printing:  'Em impressão',
  done:      'Concluído',
  cancelled: 'Cancelado',
  refunded:  'Reembolsado',
};

const STATUS_CHIPS: Record<OrderStatus, string> = {
  quote:     'chip-neutral',
  reserved:  'chip-info',
  printing:  'chip-warning',
  done:      'chip-positive',
  cancelled: 'chip-negative',
  refunded:  'chip-neutral',
};

const PAYMENT_LABELS: Record<string, string> = {
  pix:         'PIX',
  credit_card: 'Cartão de crédito',
  debit_card:  'Cartão de débito',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending:    'Pendente',
  authorized: 'Autorizado',
  paid:       'Pago',
  refunded:   'Reembolsado',
  failed:     'Falhou',
};

// Ordem natural de progresso de um pedido
const PROGRESS_ORDER: OrderStatus[] = ['quote', 'reserved', 'printing', 'done'];

// ─── Timeline ────────────────────────────────────────────────────────────────

function TimelineItem({
  label,
  timestamp,
  isActive,
  isDone,
  isLast,
}: {
  label: string;
  timestamp?: string;
  isActive?: boolean;
  isDone?: boolean;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-3">
      {/* Eixo vertical */}
      <div className="flex flex-col items-center">
        {isDone ? (
          <CheckCircle2 className="w-5 h-5 text-[#14a655] shrink-0" />
        ) : isActive ? (
          <div className="w-5 h-5 rounded-full border-2 border-[#14a655] bg-[#14a655]/20 shrink-0 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-[#14a655] animate-pulse" />
          </div>
        ) : (
          <Circle className="w-5 h-5 text-border shrink-0" />
        )}
        {!isLast && (
          <div className={cn(
            'w-0.5 flex-1 mt-1 mb-0',
            isDone ? 'bg-[#14a655]/40' : 'bg-border'
          )} style={{ minHeight: '28px' }} />
        )}
      </div>

      {/* Conteúdo */}
      <div className={cn('pb-5', isLast && 'pb-0')}>
        <p className={cn(
          'text-sm font-medium',
          isDone || isActive ? 'text-ink' : 'text-ink-soft'
        )}>
          {label}
        </p>
        {timestamp ? (
          <p className="text-xs text-ink-muted mt-0.5">{formatDate(timestamp)}</p>
        ) : isActive ? (
          <p className="text-xs text-[#14a655] mt-0.5 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Em andamento
          </p>
        ) : (
          <p className="text-xs text-ink-soft mt-0.5">Aguardando</p>
        )}
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function OrderDetail() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const { order, events, loading, error } = useOrderDetail(orderId);
  const context   = useOrderContext(orderId);
  const navigate  = useNavigate();
  const { updateStatus } = useFarmOrder();
  const [chatBusy, setChatBusy]     = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [actionErr, setActionErr]   = useState('');
  // Edição de orçamento pelo cliente (antes de 'printing') — todos os campos
  const [editing, setEditing]       = useState(false);
  const [editBusy, setEditBusy]     = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [eCosts, setECosts]         = useState<FarmCosts | null>(null);
  const [eMachines, setEMachines]   = useState<FarmMachine[]>([]);
  const [eMaterial, setEMaterial]   = useState('PLA');
  const [eHours, setEHours]         = useState(2);
  const [eGrams, setEGrams]         = useState(100);
  const [eMachineIdx, setEMachineIdx] = useState(0);
  const [eMachinesToRent, setEMachinesToRent] = useState(1);
  const [eDeliveryKm, setEDeliveryKm] = useState<number | undefined>(undefined);
  const [eQty, setEQty]             = useState(1);
  const [eColors, setEColors]       = useState('');
  const [eNotes, setENotes]         = useState('');
  const [eModeling, setEModeling]   = useState(false);
  const [eModelingType, setEModelingType] = useState<'organic' | 'parametric'>('organic');
  const [eFile, setEFile]           = useState<File | null>(null);
  const [eFileErr, setEFileErr]     = useState('');

  const isAtestado = order?.specs?.atestado_leigo === true;

  // Denúncia do pedido
  const [reporting, setReporting] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Estorno (dono da farm)
  const [refundBusy, setRefundBusy] = useState(false);

  async function handleReportOrder() {
    if (!context || !order) return;
    setReportBusy(true);
    try {
      const { data: resp, error } = await supabase.functions.invoke('report-farm', {
        body: { farm_id: context.farm_id, order_id: order.id, reason: reportReason.trim() },
      });
      if (error) throw new Error(error.message);
      if (!resp?.ok) throw new Error(resp?.error ?? 'Falha ao enviar denúncia.');
      setReportMsg({ ok: true, text: `Denúncia registrada (protocolo ${String(resp.protocol).slice(0, 8)}). O suporte fará a análise.` });
      setReportReason('');
    } catch (e: any) {
      setReportMsg({ ok: false, text: e.message ?? 'Erro ao enviar denúncia.' });
    } finally {
      setReportBusy(false);
    }
  }

  async function handleRefund() {
    if (!order) return;
    if (!window.confirm('Estornar o pagamento deste pedido? O pedido será marcado como reembolsado.')) return;
    setRefundBusy(true);
    setActionErr('');
    try {
      const { error } = await supabase
        .from('farm_orders')
        .update({ status: 'refunded', payment_status: 'refunded' } as never)
        .eq('id', order.id);
      if (error) throw new Error(error.message);
      window.location.reload();
    } catch (e: any) {
      setActionErr(e.message ?? 'Erro ao estornar.');
      setRefundBusy(false);
    }
  }

  function handleReceipt() {
    if (!order || !context) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const specs = order.specs ?? {};
    w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>Recibo ${order.id.slice(0, 8).toUpperCase()}</title>
      <style>body{font-family:system-ui,sans-serif;max-width:640px;margin:40px auto;color:#111}h1{font-size:20px}table{width:100%;border-collapse:collapse;margin:16px 0}td{padding:6px 0;border-bottom:1px solid #eee;font-size:14px}td:last-child{text-align:right}small{color:#666}.total td{font-weight:bold;border-top:2px solid #111}</style></head><body>
      <h1>Recibo de serviço — MYND Farms</h1>
      <small>Pedido #${order.id.slice(0, 8).toUpperCase()} · Emitido em ${new Date().toLocaleString('pt-BR')}</small>
      <table>
        <tr><td>Farm (fornecedor)</td><td>${context.farm_name}</td></tr>
        <tr><td>Cliente</td><td>${context.customer_label ?? '—'}</td></tr>
        <tr><td>Data do pedido</td><td>${new Date(order.created_at).toLocaleDateString('pt-BR')}</td></tr>
        <tr><td>Status</td><td>${STATUS_LABELS[order.status]} · Pagamento: ${PAYMENT_STATUS_LABELS[order.payment_status] ?? order.payment_status}</td></tr>
        ${specs.quantity != null ? `<tr><td>Quantidade de peças</td><td>${specs.quantity}</td></tr>` : ''}
        ${specs.material != null ? `<tr><td>Material</td><td>${specs.material}</td></tr>` : ''}
        <tr><td>Valor do serviço (farm)</td><td>${formatBRL(order.supplier_amount_cents)}</td></tr>
        <tr><td>Taxa da plataforma (7%) — inclui impostos e encargos da intermediação</td><td>${formatBRL(order.platform_fee_cents)}</td></tr>
        <tr class="total"><td>Total</td><td>${formatBRL(order.total_amount_cents)}</td></tr>
      </table>
      <small>Documento gerado eletronicamente pela plataforma MYND. Não substitui nota fiscal — a emissão de NF é de responsabilidade do fornecedor.</small>
      <script>window.print()</script></body></html>`);
    w.document.close();
  }

  async function openEdit() {
    if (!order) return;
    setEQty(Number(order.specs.quantity ?? 1) || 1);
    setEColors(String(order.specs.colors ?? ''));
    setENotes(String(order.specs.notes ?? ''));
    setEModeling(!!order.specs.modeling_requested);
    setEModelingType((order.specs.modeling_type as 'organic' | 'parametric') ?? 'organic');
    setEMaterial(String(order.specs.material ?? 'PLA'));
    setEHours(Number(order.specs.hours_estimated ?? 2) || 2);
    setEGrams(Number(order.specs.filament_grams ?? 100) || 100);
    setEMachinesToRent(Number(order.specs.machines_count ?? 1) || 1);
    setEDeliveryKm(order.specs.delivery_km != null ? Number(order.specs.delivery_km) : undefined);
    setEFile(null);
    setEFileErr('');
    setActionErr('');
    setEditing(true);
    // Carrega custos e máquinas da farm para recalcular preço
    setEditLoading(true);
    const [costsRes, machRes] = await Promise.all([
      supabase.from('farm_costs').select('*').eq('farm_id', order.farm_id).maybeSingle(),
      supabase.from('farm_machines').select('*').eq('farm_id', order.farm_id),
    ]);
    setECosts((costsRes.data as unknown as FarmCosts | null) ?? null);
    const machs = (machRes.data as unknown as FarmMachine[]) ?? [];
    setEMachines(machs);
    const idx = machs.findIndex((m) => m.brand === order.specs.machine_brand && m.model === order.specs.machine_model);
    setEMachineIdx(idx >= 0 ? idx : 0);
    setEditLoading(false);
  }

  async function handleSaveEdit() {
    if (!order) return;
    setEditBusy(true);
    setActionErr('');
    try {
      // Reenvia arquivo se o cliente anexou um novo
      let fileUrl = order.specs.project_file_url;
      let fileName = order.specs.project_file_name;
      if (eFile) {
        const path = `${order.farm_id}/${Date.now()}-${eFile.name.replace(/[^\w.\-]/g, '_')}`;
        const { error: upErr } = await supabase.storage
          .from('quote-files')
          .upload(path, eFile, { contentType: 'application/octet-stream' });
        if (upErr) throw new Error(`Falha ao enviar o arquivo: ${upErr.message}`);
        const { data: pub } = supabase.storage.from('quote-files').getPublicUrl(path);
        fileUrl = pub.publicUrl;
        fileName = eFile.name;
      }

      let newSpecs: Record<string, unknown>;
      let amounts: { supplier_amount_cents: number; platform_fee_cents: number; total_amount_cents: number } | null = null;

      if (isAtestado) {
        // Atestado: cliente só edita quantidade, observações e documento
        newSpecs = {
          ...order.specs,
          quantity: eQty,
          notes: eNotes.trim() || undefined,
          project_file_url: fileUrl,
          project_file_name: fileName,
        };
      } else {
        const machine = eMachines[eMachineIdx];
        const maxM = Math.max(1, machine?.quantity ?? 1);
        const rentCount = Math.min(Math.max(1, eMachinesToRent), maxM);
        const modelingFee = eModeling
          ? (eModelingType === 'organic'
              ? (eCosts?.modeling_organic_fee_cents ?? 0)
              : (eCosts?.modeling_parametric_fee_cents ?? 0))
          : 0;
        if (eCosts) {
          const pricing = calcFarmPricing({
            hourly_machine_cost_cents:  eCosts.hourly_machine_cost_cents,
            hours_estimated:            eHours,
            setup_fee_cents:            eCosts.setup_fee_cents,
            filament_grams:             eGrams,
            filament_cost_per_kg_cents: 8000,
            filament_markup_pct:        eCosts.filament_markup_pct,
            delivery_fee_base_cents:    eCosts.delivery_fee_base_cents ?? 0,
            delivery_fee_per_km_cents:  eCosts.delivery_fee_per_km_cents ?? 0,
            delivery_km:                eDeliveryKm,
            modeling_fee_cents:         modelingFee,
          });
          amounts = {
            supplier_amount_cents: pricing.supplier_amount_cents,
            platform_fee_cents:    pricing.platform_fee_cents,
            total_amount_cents:    pricing.total_amount_cents,
          };
        }
        newSpecs = {
          ...order.specs,
          material: eMaterial,
          hours_estimated: eHours,
          filament_grams: eGrams,
          machine_brand: machine?.brand,
          machine_model: machine?.model,
          machines_count: rentCount,
          wall_clock_hours: rentCount > 0 ? eHours / rentCount : eHours,
          delivery_km: eDeliveryKm,
          quantity: eQty,
          colors: eColors.trim() || undefined,
          notes: eNotes.trim() || undefined,
          modeling_requested: eModeling || undefined,
          modeling_type: eModeling ? eModelingType : undefined,
          modeling_fee_cents: eModeling ? modelingFee : undefined,
          project_file_url: fileUrl,
          project_file_name: fileName,
        };
      }

      // O trigger do banco valida o status, volta o pedido para 'quote'
      // e notifica o dono da farm automaticamente.
      const { error } = await supabase
        .from('farm_orders')
        .update({ specs: newSpecs, ...(amounts ?? {}) } as never)
        .eq('id', order.id);
      if (error) throw new Error(error.message);
      window.location.reload();
    } catch (e: any) {
      setActionErr(e.message ?? 'Erro ao salvar a edição.');
      setEditBusy(false);
    }
  }

  async function handleChat() {
    if (!context) return;
    setChatBusy(true);
    setActionErr('');
    try {
      const counterparty = context.viewer_is_owner
        ? context.customer_user_id
        : context.farm_owner_user_id;
      const threadId = await getOrCreateThread(context.farm_id, counterparty);
      navigate(`/farms/chat/${threadId}`);
    } catch (e: any) {
      setActionErr(e.message ?? 'Erro ao abrir a conversa.');
    } finally {
      setChatBusy(false);
    }
  }

  async function handleCancelQuote() {
    if (!order) return;
    setCancelBusy(true);
    setActionErr('');
    try {
      await updateStatus(order.id, 'cancelled');
      navigate('/farms/pedidos');
    } catch (e: any) {
      setActionErr(e.message ?? 'Erro ao cancelar o orçamento.');
      setCancelBusy(false);
    }
  }

  if (loading) {
    return (
      <PageShell title="Detalhes do pedido" back="/farms/pedidos">
        <div className="space-y-4">
          <div className="h-28 rounded-2xl bg-surface-subtle animate-pulse" />
          <div className="h-52 rounded-2xl bg-surface-subtle animate-pulse" />
        </div>
      </PageShell>
    );
  }

  if (error || !order) {
    return (
      <PageShell title="Detalhes do pedido" back="/farms/pedidos">
        <div className="card p-6 flex items-start gap-3 text-negative">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Pedido não encontrado</p>
            <p className="text-xs mt-1 text-ink-muted">{error ?? 'Verifique se você tem permissão para ver este pedido.'}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  const farmName = order.farms?.name ?? 'Farm';
  const farmSlug = order.farms?.slug;

  // Monta a timeline combinando eventos reais + estados futuros
  // Pedidos cancelados/reembolsados mostram apenas até o ponto de cancelamento
  const isCancelled = order.status === 'cancelled' || order.status === 'refunded';
  const progressSteps = isCancelled
    ? [...PROGRESS_ORDER, order.status]
    : PROGRESS_ORDER;

  const eventMap = new Map(events.map((e) => [e.status, e.changed_at]));
  const currentIdx = PROGRESS_ORDER.indexOf(order.status as OrderStatus);

  return (
    <PageShell
      title={`Pedido #${order.id.slice(0, 8).toUpperCase()}`}
      subtitle={`Farm: ${farmName}`}
      back="/farms/pedidos"
    >
      <div className="space-y-4">

        {/* Cabeçalho do pedido */}
        <div className="card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14a655]/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-[#14a655]" />
            </div>
            <div>
              <p className="font-semibold text-ink">{farmName}</p>
              <p className="text-xs text-ink-muted mt-0.5">{formatDate(order.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={cn(STATUS_CHIPS[order.status], 'text-sm')}>
              {isAtestado && order.status === 'reserved'
                ? (context?.viewer_is_owner ? 'Aguardando aprovação do cliente' : 'Aguardando sua aprovação')
                : STATUS_LABELS[order.status]}
            </span>
            {farmSlug && (
              <Link
                to={`/farms/${farmSlug}`}
                className="btn-secondary !py-1 !px-2.5 !text-xs gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Ver farm
              </Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Timeline */}
          <div className="card p-5">
            <p className="font-semibold text-ink mb-5">Progresso do pedido</p>

            {events.length === 0 && !isCancelled ? (
              // Sem eventos registrados: mostra timeline sintética baseada no status atual
              <div>
                {PROGRESS_ORDER.map((step, idx) => {
                  const isDone   = idx < currentIdx;
                  const isActive = idx === currentIdx;
                  const isLast   = idx === PROGRESS_ORDER.length - 1;
                  return (
                    <TimelineItem
                      key={step}
                      label={STATUS_LABELS[step]}
                      isActive={isActive}
                      isDone={isDone}
                      isLast={isLast}
                    />
                  );
                })}
              </div>
            ) : (
              // Com eventos reais do banco
              <div>
                {progressSteps.map((step, idx) => {
                  const timestamp = eventMap.get(step);
                  const isDone    = !!timestamp && step !== order.status;
                  const isActive  = step === order.status;
                  const isLast    = idx === progressSteps.length - 1;
                  return (
                    <TimelineItem
                      key={step}
                      label={STATUS_LABELS[step as OrderStatus] ?? step}
                      timestamp={timestamp}
                      isActive={isActive}
                      isDone={isDone}
                      isLast={isLast}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Resumo financeiro + ações */}
          <div className="space-y-4">

            {/* Valores */}
            <div className="card p-5 space-y-3">
              <p className="font-semibold text-ink">Resumo financeiro</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-muted">Valor à farm</span>
                  <span className="tabular-nums font-medium">{formatBRL(order.supplier_amount_cents)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-muted">Taxa da plataforma (7%)</span>
                  <span className="tabular-nums font-medium">{formatBRL(order.platform_fee_cents)}</span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between font-semibold">
                  <span className="text-ink">Total pago</span>
                  <span className="tabular-nums text-ink">{formatBRL(order.total_amount_cents)}</span>
                </div>
              </div>
            </div>

            {/* Pagamento */}
            <div className="card p-5 space-y-2">
              <p className="font-semibold text-ink flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-ink-muted" />
                Pagamento
              </p>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-ink-muted">Método</span>
                  <span>{order.payment_method ? PAYMENT_LABELS[order.payment_method] ?? order.payment_method : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-muted">Status</span>
                  <span>{PAYMENT_STATUS_LABELS[order.payment_status] ?? order.payment_status}</span>
                </div>
              </div>
            </div>

            {/* CTA: Pagar agora — SOMENTE para o cliente (nunca para o dono da farm) */}
            {context && !context.viewer_is_owner
              && order.status === 'reserved' && order.payment_status === 'pending' && (
              <Link
                to={`/farms/checkout/${order.id}`}
                className="card p-4 flex items-center gap-3 hover:shadow-md transition-shadow group bg-[#14a655]/5 border border-[#14a655]/30"
              >
                <div className="w-9 h-9 rounded-xl bg-[#14a655]/15 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-[#14a655]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#14a655]">Cotação aceita — Pagar agora</p>
                  <p className="text-xs text-ink-muted">Clique para concluir o pagamento</p>
                </div>
              </Link>
            )}

            {/* Visão do dono da farm: status do recebimento, sem ações de pagamento */}
            {context?.viewer_is_owner && order.status === 'reserved' && (
              <div className="card p-4 flex items-center gap-3 bg-surface-subtle">
                <div className="w-9 h-9 rounded-xl bg-warning/15 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-warning" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink">
                    {order.payment_status === 'pending' ? 'Aguardando pagamento do cliente' : 'Pagamento em processamento'}
                  </p>
                  <p className="text-xs text-ink-muted">O status atualiza automaticamente quando o cliente pagar.</p>
                </div>
              </div>
            )}

            {/* Especificações do orçamento */}
            {order.specs && Object.keys(order.specs).length > 0 && (
              <div className="card p-5 space-y-2">
                <p className="font-semibold text-ink flex items-center gap-2">
                  <Printer className="w-4 h-4 text-ink-muted" />
                  Especificações do orçamento
                </p>
                <div className="text-sm space-y-1">
                  {order.specs.atestado_leigo === true && (
                    <div className="rounded-xl bg-warning/10 border border-warning/20 px-3 py-2 text-xs text-ink-muted">
                      📝 Atestado de leigo — a farm monta o orçamento a partir do documento enviado.
                    </div>
                  )}
                  {order.specs.hours_estimated != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Horas de máquina</span>
                      <span>{String(order.specs.hours_estimated)}h</span>
                    </div>
                  )}
                  {order.specs.machines_count != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Máquinas alugadas</span>
                      <span>{String(order.specs.machines_count)}{order.specs.wall_clock_hours != null ? ` · ~${Number(order.specs.wall_clock_hours).toFixed(1)}h de prazo` : ''}</span>
                    </div>
                  )}
                  {order.specs.material != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Material</span>
                      <span>{MATERIALS.find((m) => m.value === String(order.specs.material))?.label ?? String(order.specs.material)}</span>
                    </div>
                  )}
                  {order.specs.filament_grams != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Filamento estimado</span>
                      <span>{String(order.specs.filament_grams)} g</span>
                    </div>
                  )}
                  {(order.specs.machine_brand != null || order.specs.machine_model != null) && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Máquina</span>
                      <span>{[order.specs.machine_brand, order.specs.machine_model].filter(Boolean).map(String).join(' ')}</span>
                    </div>
                  )}
                  {order.specs.delivery_km != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Entrega</span>
                      <span>{String(order.specs.delivery_km)} km</span>
                    </div>
                  )}
                  {order.specs.quantity != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Quantidade de peças</span>
                      <span>{String(order.specs.quantity)}</span>
                    </div>
                  )}
                  {order.specs.supplier_deadline_days != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Prazo informado pela farm</span>
                      <span className="font-medium">{String(order.specs.supplier_deadline_days)} dia(s)</span>
                    </div>
                  )}
                  {order.specs.supplier_delivery_method != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Forma de entrega</span>
                      <span>
                        {order.specs.supplier_delivery_method === 'retirada' ? 'Retirada no local'
                          : order.specs.supplier_delivery_method === 'entrega_local' ? 'Entrega local'
                          : 'Envio (Correios/transportadora)'}
                      </span>
                    </div>
                  )}
                  {order.specs.colors != null && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Cores</span>
                      <span className="text-right max-w-[60%]">{String(order.specs.colors)}</span>
                    </div>
                  )}
                  {order.specs.modeling_requested === true && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Serviço de modelagem 3D</span>
                      <span className="text-brand-700 font-medium">
                        {order.specs.modeling_type === 'parametric' ? 'Paramétrica (CAD)'
                          : order.specs.modeling_type === 'organic' ? 'Orgânica'
                          : 'Solicitado'}
                      </span>
                    </div>
                  )}
                  {order.specs.notes != null && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-ink-muted text-xs mb-1">Observações do cliente</p>
                      <p className="whitespace-pre-wrap">{String(order.specs.notes)}</p>
                    </div>
                  )}
                  {order.specs.project_file_url != null && (
                    <div className="pt-2 border-t border-border">
                      <a
                        href={String(order.specs.project_file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-700 underline flex items-center gap-1.5"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        {String(order.specs.project_file_name ?? 'Arquivo do projeto')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Solicitante (visível para o dono da farm) */}
            {context?.viewer_is_owner && (
              <div className="card p-5 space-y-1">
                <p className="font-semibold text-ink flex items-center gap-2">
                  <User className="w-4 h-4 text-ink-muted" />
                  Solicitante
                </p>
                <p className="text-sm text-ink">{context.customer_label ?? 'Cliente'}</p>
              </div>
            )}

            {actionErr && (
              <div className="flex items-center gap-2 text-xs text-negative bg-negative/10 border border-negative/20 rounded-xl px-3 py-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />{actionErr}
              </div>
            )}

            {/* Ação: abrir chat com a outra parte */}
            {context && (
              <button
                onClick={handleChat}
                disabled={chatBusy}
                className="card p-4 flex items-center gap-3 hover:shadow-md transition-shadow group w-full text-left"
              >
                <div className="w-9 h-9 rounded-xl bg-surface-subtle flex items-center justify-center group-hover:bg-[#14a655]/10 transition-colors">
                  {chatBusy
                    ? <Loader2 className="w-4 h-4 animate-spin text-ink-muted" />
                    : <MessageCircle className="w-4 h-4 text-ink-muted group-hover:text-[#14a655] transition-colors" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink">
                    {context.viewer_is_owner
                      ? `Conversar com ${context.customer_label ?? 'o cliente'}`
                      : `Conversar com ${context.farm_name}`}
                  </p>
                  <p className="text-xs text-ink-muted">Abre o chat desta negociação</p>
                </div>
              </button>
            )}

            {/* Ação: editar orçamento (cliente, antes da impressão) */}
            {context && !context.viewer_is_owner
              && (order.status === 'quote' || order.status === 'reserved') && (
              <button
                onClick={openEdit}
                className="btn-secondary w-full gap-2"
              >
                <Pencil className="w-4 h-4" />
                Editar orçamento
              </button>
            )}

            {/* Ação: cancelar orçamento (cliente, antes do pagamento) */}
            {context && !context.viewer_is_owner
              && (order.status === 'quote' || (order.status === 'reserved' && order.payment_status === 'pending')) && (
              <button
                onClick={handleCancelQuote}
                disabled={cancelBusy}
                className="btn-secondary w-full gap-2 text-negative border-negative/30"
              >
                <XCircle className="w-4 h-4" />
                {cancelBusy ? 'Cancelando…' : 'Cancelar orçamento'}
              </button>
            )}

            {/* Recibo (pagamento efetuado ou pedido concluído) */}
            {(['authorized', 'paid', 'refunded'].includes(order.payment_status) || order.status === 'done') && (
              <button onClick={handleReceipt} className="btn-secondary w-full gap-2">
                <Receipt className="w-4 h-4" />
                Gerar recibo
              </button>
            )}

            {/* Estorno (dono da farm, pagamento autorizado/pago) */}
            {context?.viewer_is_owner
              && ['authorized', 'paid'].includes(order.payment_status)
              && order.status !== 'refunded' && (
              <button
                onClick={() => void handleRefund()}
                disabled={refundBusy}
                className="btn-secondary w-full gap-2 text-negative border-negative/30"
              >
                <RotateCcw className="w-4 h-4" />
                {refundBusy ? 'Estornando…' : 'Estornar pagamento'}
              </button>
            )}

            {/* Denunciar pedido */}
            {context && (
              <button
                onClick={() => { setReporting(true); setReportMsg(null); }}
                className="btn-ghost w-full !py-1.5 !text-xs gap-1.5 text-ink-muted hover:text-negative"
              >
                <Flag className="w-3.5 h-3.5" />
                Denunciar este pedido
              </button>
            )}

          </div>
        </div>
      </div>

      {/* Modal de denúncia do pedido */}
      {reporting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !reportBusy && setReporting(false)}>
          <div className="card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-ink flex items-center gap-2">
                <Flag className="w-4 h-4 text-negative" />
                Denunciar pedido #{order.id.slice(0, 8).toUpperCase()}
              </h3>
              <button onClick={() => setReporting(false)} disabled={reportBusy} className="text-ink-muted hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>
            {reportMsg ? (
              <>
                <div className={`rounded-xl px-4 py-3 text-sm ${reportMsg.ok ? 'bg-positive/10 text-positive border border-positive/20' : 'bg-negative/10 text-negative border border-negative/20'}`}>
                  {reportMsg.text}
                </div>
                <button onClick={() => setReporting(false)} className="btn-secondary w-full">Fechar</button>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-muted">
                  Descreva o problema com este pedido. A denúncia é registrada e enviada
                  ao suporte para análise (descrição obrigatória).
                </p>
                <textarea
                  className="input w-full"
                  rows={5}
                  maxLength={2000}
                  placeholder="Descreva o ocorrido (mínimo 10 caracteres)…"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleReportOrder()}
                    disabled={reportBusy || reportReason.trim().length < 10}
                    className="btn-primary flex-1 gap-2"
                  >
                    {reportBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {reportBusy ? 'Enviando…' : 'Enviar denúncia'}
                  </button>
                  <button onClick={() => setReporting(false)} disabled={reportBusy} className="btn-secondary">Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de edição do orçamento */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !editBusy && setEditing(false)}>
          <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-ink">Editar orçamento</h3>
              <button onClick={() => setEditing(false)} disabled={editBusy} className="text-ink-muted hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-ink-muted rounded-xl bg-warning/10 border border-warning/20 px-3 py-2">
              Ao salvar, o pedido volta para a etapa de análise e o dono da farm será
              notificado para aceitar ou recusar a nova cotação.
            </p>

            {editLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-ink-muted" /></div>
            ) : isAtestado ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-ink-muted mb-1">Quantidade de peças</label>
                  <input type="number" min={1} className="input w-full" value={eQty}
                    onChange={(e) => setEQty(Math.max(1, Number(e.target.value)))} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-ink-muted mb-1">Observações para a farm</label>
                  <textarea className="input w-full" rows={3} maxLength={600} value={eNotes}
                    onChange={(e) => setENotes(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Material</label>
                  <select className="input w-full" value={eMaterial} onChange={(e) => setEMaterial(e.target.value)}>
                    {MATERIALS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Máquina</label>
                  <select className="input w-full" value={eMachineIdx}
                    onChange={(e) => { setEMachineIdx(Number(e.target.value)); setEMachinesToRent(1); }}>
                    {eMachines.map((m, i) => (
                      <option key={m.id} value={i}>{m.brand} {m.model} (×{m.quantity})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Horas de impressão (em 1 máquina)</label>
                  <input type="number" min={0.5} step={0.5} className="input w-full" value={eHours}
                    onChange={(e) => setEHours(Number(e.target.value))} />
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">
                    Máquinas a alugar (máx. {Math.max(1, eMachines[eMachineIdx]?.quantity ?? 1)})
                  </label>
                  <select className="input w-full" value={Math.min(eMachinesToRent, Math.max(1, eMachines[eMachineIdx]?.quantity ?? 1))}
                    onChange={(e) => setEMachinesToRent(Number(e.target.value))}>
                    {Array.from({ length: Math.max(1, eMachines[eMachineIdx]?.quantity ?? 1) }).map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Filamento estimado (g)</label>
                  <input type="number" min={1} className="input w-full" value={eGrams}
                    onChange={(e) => setEGrams(Number(e.target.value))} />
                </div>
                {eCosts?.delivery_offered && (
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Entrega (km) — opcional</label>
                    <input type="number" min={0} className="input w-full" value={eDeliveryKm ?? ''}
                      onChange={(e) => setEDeliveryKm(e.target.value ? Number(e.target.value) : undefined)} />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Quantidade de peças</label>
                  <input type="number" min={1} className="input w-full" value={eQty}
                    onChange={(e) => setEQty(Math.max(1, Number(e.target.value)))} />
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Cores desejadas</label>
                  <input type="text" className="input w-full" maxLength={120} value={eColors}
                    onChange={(e) => setEColors(e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-ink-muted mb-1">Observações para a farm</label>
                  <textarea className="input w-full" rows={3} maxLength={600} value={eNotes}
                    onChange={(e) => setENotes(e.target.value)} />
                </div>
                <div className="col-span-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                    <input type="checkbox" checked={eModeling} onChange={(e) => setEModeling(e.target.checked)} />
                    Solicitar serviço de modelagem 3D
                  </label>
                  {eModeling && (
                    <select className="input w-full" value={eModelingType}
                      onChange={(e) => setEModelingType(e.target.value as 'organic' | 'parametric')}>
                      {MODELING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  )}
                </div>
              </div>
            )}

            {!editLoading && (
              <div>
                <label className="block text-xs text-ink-muted mb-1">
                  Arquivo do projeto {(isAtestado || eModeling) ? <span className="text-negative">*</span> : '(opcional — substitui o atual)'}
                  <span className="block text-[11px] text-ink-soft">Aceita: {QUOTE_FILE_HINT}. Máx. 50 MB.</span>
                </label>
                <input
                  type="file"
                  accept={QUOTE_FILE_ACCEPT}
                  className="block w-full text-xs text-ink-muted file:mr-3 file:btn-secondary file:!py-1.5 file:!px-3 file:!text-xs file:border-0 file:rounded-xl file:cursor-pointer"
                  onChange={(e) => {
                    setEFileErr('');
                    const f = e.target.files?.[0] ?? null;
                    if (f && !QUOTE_FILE_RE.test(f.name)) { setEFileErr(`Formato não aceito. Envie: ${QUOTE_FILE_HINT}.`); e.target.value = ''; return; }
                    if (f && f.size > 50 * 1024 * 1024) { setEFileErr('Arquivo muito grande (máx. 50 MB).'); e.target.value = ''; return; }
                    setEFile(f);
                  }}
                />
                {eFile
                  ? <p className="text-xs text-positive mt-1">{eFile.name} pronto para envio.</p>
                  : order.specs.project_file_name != null && <p className="text-xs text-ink-soft mt-1">Atual: {String(order.specs.project_file_name)}</p>}
                {eFileErr && <p className="text-xs text-negative mt-1">{eFileErr}</p>}
              </div>
            )}
            <p className="text-xs text-ink-soft">
              O valor do orçamento é recalculado automaticamente com base nos campos acima.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => void handleSaveEdit()}
                disabled={editBusy || editLoading || ((isAtestado || eModeling) && !eFile && order.specs.project_file_url == null)}
                className="btn-primary flex-1 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                {editBusy ? 'Salvando…' : 'Salvar e reenviar para análise'}
              </button>
              <button onClick={() => setEditing(false)} disabled={editBusy} className="btn-secondary">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
